import React from 'react';
import {observer} from 'mobx-react';
import store from '@lib/store/configuration';
import Warning from '../../components/common/Warning';
import ConfigurationEditorGeneralInformationCard from './EditorGeneralInformationCard';
import ConfigurationEditorTechnicalInformationCard from './EditorTechnicalInformationCard';
import EditorCommentCard from './EditorCommentCard';
import FilesCard from '../file/FilesCard'
import ConfigurationEditorStepUnitType from './steps/StepUnitType';
import ConfigurationEditorStepHeatingTWS from './steps/StepHeatingTWS';
import {StepHeating1, StepHeating2, StepHeating3} from './steps/StepHeating';
import ConfigurationEditorStepDHW from './steps/StepDHW';
import ConfigurationEditorStepOptions from './steps/StepOptions';
import ConfigurationEditorStepSummary from './steps/StepSummary';
import ConfigurationEditorStepController from './steps/StepController';
import ConfigurationEditorStepPricing from './steps/StepPricing';
import ConfigurationEditorStepTopArticleNumberPricingStep from './steps/StepTopArticleNumber';
import ConfigurationEditorStepPrimarySideEquipment from './steps/StepPrimarySideEquipment';
import ConfigurationAPIService from '@lib/api-services/ConfigurationAPIService';
import APIServicesRegistry from '@lib/api-services/APIServicesRegistry';
import ExpandableContainer from './steps/ExpandableContainer';
import EditorDiagramCard from './EditorDiagramCard';
import EditorNavigationButtons from './EditorNavigationButtons';
import Configurator from '../../model/configurator/Configurator';
import EESTable from './EESTable';
import IssuesList from '../../model/configurator/IssuesList';
import Issue from '../../model/configurator/Issue';
import WarningAlert from './alerts/WarningAlert';
import EditorConnectionLostAlert from './alerts/ConnectionLostAlert';
import EditorRestoreFromSnapshotAlert from './alerts/EditorRestoreFromSnapshotAlert';
import EditorSnapshotLoadErrorAlert from './alerts/EditorSnapshotLoadErrorAlert';
import FieldExtensions from './fields/FieldExtensions';
import EditorMobileNavigation from './EditorMobileNavigation';
import HeatingOutputField from '../../model/configurator/HeatingOutputField';
import StepsTitleBar from "@lib/components/shared/steps/StepsTitleBar";

// TODO: This component is waaay too big.

const ConfigurationEditor = observer(
    class ConfigurationEditor extends React.Component {

        static defaultProps = {
            configurationAPIService: ConfigurationAPIService
        }

        stepAreaRef = React.createRef()
        stepAreaColumnScrollRef = React.createRef()
        stepTitleBarRef = React.createRef()
        navigationButtonRef = React.createRef();

        formModelUpdateTimeout = -1
        lastSentModel = {};
        busy = false

        steps = []
        state = {
            currentStepId: 'unitType',
            currentStepIdx: 0,
            isLoading: false,
            stepAreaContainerX: 0,
            realStepIdx: 0,
            isConnectionAlertVisible: false,
            wasSnapshotTaken: false,
            suggestRestoreFromSnapshot: false,
            isSnapshotLoadErrorVisible: false,
            isControlCenterWarningVisible: false,
            maxPlatesExceededAlertSeen: false,
            visitedControlCenters: []
        }

        constructor(props) {
            super(props)

            window.addEventListener('resize', this.onWindowResize)

            if (window.localStorage) {
                let suggestRestore = (window.localStorage.getItem('editor.suggestSnapshotRestore') === 'true')
                if (suggestRestore) {
                    this.state = {
                        ...this.state,
                        snapshotTime: parseInt(window.localStorage.getItem('editor.snapshotTime')),
                        suggestRestoreFromSnapshot: suggestRestore
                    }
                }
            }

            scheduleAtDocumentReady(() => {
                this.configurationAPIService = APIServicesRegistry.getService(this.props.configurationAPIService)
                this.moveThroughStepTimeline(0)
            })

            window.onbeforeunload = e => {
                e = e || window.event;

                if (store.dirty) {
                    const message = __('Changes that you have made will not be saved.');

                    if (e) {
                        e.returnValue = message;
                    }

                    return message;
                }
            }

            this.configurator = new Configurator(this)
            this.configurator.onFieldTabFocus = (field, e) => {
            };

            this.responsesToIgnore = 0
            this.canIgnoreResponse = true
            // Initialize all possible steps.
            this.addStep('unitType', ConfigurationEditorStepUnitType)
            this.addStep('heatingTWS', ConfigurationEditorStepHeatingTWS)
            this.addStep('heating1', StepHeating1)
            this.addStep('heating2', StepHeating2)
            this.addStep('heating3', StepHeating3)
            this.addStep('dhw', ConfigurationEditorStepDHW)
            this.addStep('controller', ConfigurationEditorStepController)
            this.addStep('primarySideEquipment', ConfigurationEditorStepPrimarySideEquipment)
            this.addStep('optionsAndAccessories', ConfigurationEditorStepOptions)
            this.addStep('pricing', ConfigurationEditorStepPricing)
            this.addStep('topArticleNumberPricing', ConfigurationEditorStepTopArticleNumberPricingStep)
            this.addStep('summary', ConfigurationEditorStepSummary)

            // Fill store with initial data.
            this.updateStore(this.props);
            this.lastSentModel = store.getTransformedFormModel();
            let dirty = false;

            ([1, 2, 3]).forEach(heatingIndex => {
                let heatingModel = this.props[`heating${heatingIndex}`]
                if (heatingModel) {
                    if (this.isNewConfiguration()) {
                        let currentQuickStepSetup = heatingModel.quickStepSetup.items.find(x => x.value === heatingModel.quickStepSetup.value)
                        if (currentQuickStepSetup) {
                            let attributes = currentQuickStepSetup.attributes;
                            if (attributes) {
                                let storeRef = store.formModel[`heating${heatingIndex}`]
                                storeRef.primary.inletTemperature.value = attributes['primary.InletTemperature']
                                storeRef.primary.outletTemperature.value = attributes['primary.OutletTemperature']
                                storeRef.secondary.inletTemperature.value = attributes['secondary.InletTemperature']
                                storeRef.secondary.outletTemperature.value = attributes['secondary.OutletTemperature']
                                dirty = true
                            }
                        }
                    }

                    // Set heat exchanger calculation output if possible.
                    if (!this.isNewConfiguration() && Object.keys(heatingModel.heatExchangerOutput).length > 0) {
                        store.setHeatingCalculationOutput(heatingIndex, {
                            heatExchangerOutput: heatingModel.heatExchangerOutput
                        })
                    }
                }
            })

            if (dirty) {
                // Wait for the API service to initialize.
                scheduleAtDocumentReady(() => {
                    this.onFormModelChange()
                })
            }

            this.onFormModelChange = this.onFormModelChange.bind(this)
        }

        componentDidCatch(error, info) {
            if (this.isLoadingSnapshot) {
                this.updateStore(this.props);
                this.lastSentModel = store.getTransformedFormModel();
                this.setState({
                    isSnapshotLoadErrorVisible: true
                });
                this.isLoadingSnapshot = false;
            }
        }

        componentDidMount() {
            this.evenOutCards();
        }

        /**
         * Adds a step with specified data to the list.
         *
         * @param {String} slug
         * @param {React.Component} component
         */
        addStep(slug, component) {
            this.steps.push({slug, component, ref: React.createRef()})
        }

        onWindowResize = e => {
            this.moveThroughStepTimeline(0)
        }

        onStepAreaColumnScroll = e => {
            if (e.target === this.stepAreaColumnScrollRef) {
                e.target.scrollLeft = 0;
            }
        }

        onAPIResponseReceived = response => {
            this.setState({isLoading: false})
            this.updateStore(response.data)
        }

        updateStore(data) {
            if (!data.configuratorSteps) {
                console.warn('Malformed API response received. Skipping.')
                return
            }

            // MobX uses proxy wrappers to store variables so basic comparison won't work. 
            if (JSON.stringify(store.availableSteps) !== JSON.stringify(data.configuratorSteps)) {

                // Add new steps to the configurator model before re-rendering the editor,
                // since rendering may need these steps to exist already.
                data.configuratorSteps.forEach(step => {
                    step = this.steps.find(x => x.slug === step.slug)
                    if (!step.ref.current) {
                        let model = step.component.getStepModel(data, {dddd: 'dupka'})
                        if (model) {
                            let fields = step.component.getFields(model)

                            model.initialize(
                                fields,
                                step.component.getFirstVisibleField(fields),
                                step.component.getLastVisibleField(fields)
                            );

                            this.configurator.getStepsManager().addStepModel(model.name, model)
                        }
                    }
                })

            }

            store.incrementFormModelVersion()

            data = {...data}
            data.__version = store.getFormModelVersion()
            store.setFormModel(data)
            store.setAvailableSteps(data.configuratorSteps)
            store.setIsCustom(data.unitType.isCustomLogic.value)
            // Build issues list.
            let issuesList = new IssuesList()
            this.configurator.getStepsManager().getAll().forEach(step => {
                step.setIssues([])
                let fields = step.fields
                let allFields = []

                fields.forEach(field => {
                    if (field.__collection) {
                        field.getContaintedFields().forEach(f => allFields.push(f))
                    } else {
                        allFields.push(field)
                    }
                })

                allFields.filter(x => x.visible && x.hasWarning()).forEach(field => {
                    issuesList.add(new Issue(field))
                })

                this.addCustomIssues(step, issuesList);
            })

            issuesList.getCategorizedByStep().forEach(list => {
                list.step.setIssues(list.items)
            })

            store.setIssues(issuesList)

        }

        addCustomIssues = (step, list) => {
            if (['heating1', 'heating2', 'heating3', 'dhw'].includes(step.name)) {
                for (const [index, output] of step.model.heatExchangerOutput.entries()) {
                    if (output.primaryWarning) {
                        let primaryField = new HeatingOutputField(index, 'primary', output.name, step);
                        list.add(new Issue(primaryField));
                    }
                    if (output.secondaryWarning) {
                        let secondaryField = new HeatingOutputField(index, 'secondary', output.name, step);
                        list.add(new Issue(secondaryField));
                    }
                }
            }
        }

        onNextStepButtonClick = () => {
            if (this.isOnLastStep()) {
                this.props.save()
                return
            }

            this.moveThroughStepTimeline(+1);

            let navigationBarHeight = 50;
            let stepAreaElement = this.stepAreaRef.current;
            if (stepAreaElement.getBoundingClientRect().top < navigationBarHeight) {
                window.scrollTo({
                    top: window.pageYOffset + stepAreaElement.getBoundingClientRect().top - (navigationBarHeight * 6),
                    behavior: 'smooth'
                });
            }
        }

        onPreviousStepButtonClick = () => {
            this.moveThroughStepTimeline(-1)
        }

        moveThroughStepTimeline = amount => {
            // Step index out of bounds.
            if (this.state.currentStepIdx + amount > store.getAvailableSteps().length ||
                this.state.currentStepIdx + amount < 0) {
                return;
            }

            let currentStep = this.steps.find(x => x.slug === this.state.currentStepId)
            let targetStep = this.steps.find(x => x.slug === store.getAvailableSteps()[this.state.currentStepIdx + amount].slug)

            if (currentStep.ref && currentStep.ref.current) {
                currentStep.ref.current.stepAboutToBecomeInvisible()
            }

            if (targetStep.ref && targetStep.ref.current) {
                targetStep.ref.current.stepAboutToBecomeVisible()
            }

            this.setState({
                currentStepId: targetStep.slug,
                currentStepIdx: this.state.currentStepIdx + amount
            })
        }

        onFormModelChange() {
            store.setDirty(true);
            this.scheduleFormModelUpdate()
        }

        onConnectionAlertTryAgainClick = () => {
            this.scheduleFormModelUpdate()
            this.setState({
                isConnectionAlertVisible: true,
                isLoading: true
            })
        }

        onSnapshotRestoreLoadClick = () => {
            this.isLoadingSnapshot = true;
            scheduleAtNextTick(() => {
                this.isLoadingSnapshot = false;
            })
            let snapshot = JSON.parse(window.localStorage.getItem('editor.snapshot'))

            let formModel = snapshot.lastSuccessfulResponse
            //this is bug - props contain new model steps instead snapshot
            //formModel.configuratorSteps = this.props.configuratorSteps

            formModel.configuratorSteps = snapshot.lastSuccessfulResponse.configuratorSteps;
            // Find differences between the snapshot's model and current form model.
            let currentFormModel = this.props;
            let snapshotFormModel = formModel;
            //Attempts to fix the snapshot in case of properties being changed or removed.
            const fixMissingProps = (obj, obj2) => {
                for (let [k, v] of Object.entries(obj)) {

                    // Ignore props that don't relate to the data in any way.
                    if (['generateDocumentationAccess', 'save'].includes(k)) {
                        continue;
                    }

                    if (!(k in obj2) || obj2[k] === null) {
                        obj2[k] = v;
                    } else {
                        if (typeof v === 'object' && v !== null) {
                            fixMissingProps(v, obj2[k])
                        }
                    }
                }
            }

            fixMissingProps(currentFormModel, snapshotFormModel);
            this.onAPIResponseReceived({data: snapshotFormModel})
            this.scheduleFormModelUpdate();

            this.lastSentModel = snapshot.lastSuccessfulRequest
            this.lastReceivedModel = snapshotFormModel

            this.moveThroughStepTimeline(snapshot.currentStepIdx);
            window.localStorage.setItem('editor.suggestSnapshotRestore', false)
            this.setState({
                suggestRestoreFromSnapshot: false
            })
        }

        onSnapshotRestoreIgnoreClick = () => {
            window.localStorage.setItem('editor.suggestSnapshotRestore', false)
            this.setState({
                suggestRestoreFromSnapshot: false
            })
        }

        scheduleFormModelUpdate = () => {
            const func = () => {

                if (this.busy && this.canIgnoreResponse) {
                    this.responsesToIgnore++
                }

                // The server has not responded, yet. Need to wait a little longer.
                if (new Date().getTime() - this.lastFormModelRequestTime < 200 || this.busy) {
                    clearInterval(this.formModelUpdateTimeout)
                    this.formModelUpdateTimeout = setTimeout(func, 200)
                    this.canIgnoreResponse = false

                    return
                }

                this.canIgnoreResponse = true
                this.busy = true

                // Show loader only if we've been waiting for the response for longer than 150ms.
                setTimeout(() => {

                    // Next N responses will be ignored so there's no point in showing loader.
                    if (this.busy && this.responsesToIgnore === 0) {
                        this.setState({isLoading: true})
                    }
                }, 150)

                this.lastFormModelRequestTime = new Date().getTime()
                let model = store.getTransformedFormModel();
                let payload = {
                    model: model,
                    changedInput: store.recentlyChangedField
                }

                this.configurationAPIService.configure(payload).then(response => {
                    this.busy = false;

                    if (this.responsesToIgnore > 0) {
                        this.responsesToIgnore--
                        this.setState({isLoading: false})
                        return;
                    }

                    if (window.localStorage) {
                        window.localStorage.setItem('editor.suggestSnapshotRestore', false);
                    }

                    this.lastReceivedModel = response.data;
                    this.lastSentModel = model;
                    this.onAPIResponseReceived(response);
                    this.setState({
                        isLoading: false,
                        isConnectionAlertVisible: false
                    }, () => {
                        this.navigationButtonRef.current.onWindowScroll();
                    })

                    this.handleControlCenterWarningPopUp();
                }).catch(err => {
                    let wasSnapshotTaken = false;
                    // Do a snapshot of current configuration and save it in case it's lost if possible.
                    if (window.localStorage && this.lastReceivedModel) {
                        let snapshot = {
                            lastSuccessfulRequest: this.lastSentModel,
                            lastSuccessfulResponse: this.lastReceivedModel,
                            currentStepIdx: this.state.currentStepIdx,
                            configuratorSteps: this.props.configuratorSteps,
                        };

                        window.localStorage.setItem('editor.suggestSnapshotRestore', true)
                        window.localStorage.setItem('editor.snapshotTime', new Date().getTime())
                        window.localStorage.setItem('editor.snapshot', JSON.stringify(snapshot))
                        wasSnapshotTaken = true;
                    }
                    ;

                    this.setState({
                        isLoading: false,
                        isConnectionAlertVisible: true,
                        wasSnapshotTaken: wasSnapshotTaken
                    })

                    this.busy = false
                })
            }

            func()
        }

        handleControlCenterWarningPopUp = () => {
            const limitExceeded = this.lastReceivedModel.controller.availableAnalogInputsExceeded.value;
            const selectedControlCenter = this.lastReceivedModel.controller.controlCenter.value;

            if (limitExceeded && !this.state.visitedControlCenters.includes(selectedControlCenter)) {
                this.setState({
                    isControlCenterWarningVisible: true,
                    visitedControlCenters: [...this.state.visitedControlCenters, selectedControlCenter]
                });
            }
        }

        onProjectFilesChange = (files) => {
            let formModel = store.formModel;
            formModel.files.items = files;
            store.setFormModel(formModel);
        }

        evenOutCards() {
            const left = document.getElementById("multicard-left").firstChild;
            const right = document.getElementById("multicard-right").firstChild;

            if (left.offsetHeight > right.offsetHeight) {
                right.style.minHeight = `${left.offsetHeight}px`;
            } else {
                left.style.minHeight = `${right.offsetHeight}px`;
            }
        }

        render() {
            return (
                <div className="configuration-editor-root">
                    <div className="spaced-items-group-xs row row-cards-container row-no-outer-gutter">
                        <div className="spaced-item column-6 column-xs-12">
                            <div id="multicard-left" className="multicard">
                                <ConfigurationEditorGeneralInformationCard model={this.props.generalInformation}
                                                                           onFormModelChange={this.onFormModelChange}/>
                                <FilesCard
                                    id={this.props.id.value}
                                    setDirty={() => store.setDirty(true)}
                                    helpInfo={store.formModel.filesHelpInfo.value}
                                    duringEdit={store.filesDuringEdit}
                                    setDuringEdit={value => store.setFilesDuringEdit(value)}
                                    initialFiles={this.props.files.items}
                                    initialItemsViewModel={this.props.files.itemsViewModel}
                                    allowEdit={store.editableMode}
                                    onFilesChange={this.onProjectFilesChange}
                                    documentApiService={ConfigurationAPIService}
                                />
                            </div>
                        </div>

                        <div className="spaced-item column-6 column-xs-12">
                            <div id="multicard-right" className="multicard">
                                <ConfigurationEditorTechnicalInformationCard
                                    helpInfo={store.formModel.technicalInformationHelpInfo.value}
                                    model={store.getPricing()}
                                    onFormModelChange={this.onFormModelChange}
                                />
                                <EditorCommentCard/>
                            </div>
                        </div>
                    </div>

                    <StepsTitleBar
                        activeAvailableSteps={store.getAvailableSteps()}
                        steps={store.getAvailableSteps().map(s => 
                            ({isValid: true, name: s.name, slug: s.slug, canProceedHere: true, available: true}))
                        } 
                        currentStepIndex={this.state.currentStepIdx}
                        stepsManager={this.configurator.getStepsManager()} currentStepId={this.state.currentStepId}
                        moveThroughStepTimeline={this.moveThroughStepTimeline}/>
                    {this.renderMainArea()}

                    {!this.state.isConnectionAlertVisible && !this.state.suggestRestoreFromSnapshot && (
                        this.renderStepNavigationButtons()
                    )}
                </div>
            )
        }

        renderStepNavigationButtons() {
            let issues = store.issues.issues;
            if (issues.length > 0)
                issues = issues.filter(x => x.field.step.name == this.state.currentStepId);


            const previousButtonAvailable = this.state.currentStepIdx > 0;
            const props = {
                currentStep: this.state.currentStepId,
                onPreviousStepButtonClick: this.onPreviousStepButtonClick,
                onNextStepButtonClick: this.onNextStepButtonClick,
                isPreviousButtonActive: previousButtonAvailable,
                isOnLastStep: this.isOnLastStep(),
                issues: issues,
            }

            return (
                <React.Fragment>
                    <EditorNavigationButtons
                        ref={this.navigationButtonRef}
                        {...props}
                        currentStepId={this.state.currentStepId}
                    />

                    <EditorMobileNavigation
                        {...props}
                        configurator={this.configurator}
                    />
                </React.Fragment>
            )
        }

        renderMainArea() {
            const isTargetStepPricingOrSummary = ['pricing', 'topArticleNumberPricing', 'summary'].includes(this.state.currentStepId)
            const heating1CalculationOutput = store.getHeatingCalculationOutput(1)
            const heating2CalculationOutput = store.getHeatingCalculationOutput(2)
            const heating3CalculationOutput = store.getHeatingCalculationOutput(3)
            const heatingTWSCalculationOutput = store.getHeatingTWSCalculationOutput()
            const eesLevel = store.getEESLevel()
            const dhwCalculationOutput = store.getDHWCalculationOutput()
            const heatingOutputVisible = store.getHeatingCalculationOutputVisible()
            const heatingTWSOutputVisible = store.getHeatingTWSCalculationOutputVisible()
            const dhwOutputVisible = store.getDHWCalculationOutputVisible()
            const anyHeatingCalculationOutputs = (
                (heating1CalculationOutput !== null ||
                    heating2CalculationOutput !== null ||
                    heating3CalculationOutput !== null ||
                    heatingTWSCalculationOutput !== null ||
                    dhwCalculationOutput !== null)
                && (heatingOutputVisible || heatingTWSOutputVisible || dhwOutputVisible)
            );

            const isCustom = store.getIsCustom();

            return (
                <div
                    className={`row row-no-outer-gutter configuration-editor-main-area ${['pricing', 'topArticleNumberPricing'].includes(this.state.currentStepId) ? 'configuration-editor-main-area--pricing' : ''}`}>
                    {this.state.isConnectionAlertVisible && (
                        <EditorConnectionLostAlert snapshotTaken={this.state.wasSnapshotTaken}
                                                   onTryAgainClick={this.onConnectionAlertTryAgainClick}/>
                    )}

                    {this.state.suggestRestoreFromSnapshot && (
                        <EditorRestoreFromSnapshotAlert snapshotTime={this.state.snapshotTime}
                                                        onLoadSnapshotClick={this.onSnapshotRestoreLoadClick}
                                                        onIgnoreClick={this.onSnapshotRestoreIgnoreClick}/>
                    )}

                    {this.state.isSnapshotLoadErrorVisible && (
                        <EditorSnapshotLoadErrorAlert
                            onCloseClick={() => this.setState({isSnapshotLoadErrorVisible: false})}/>
                    )}

                    {this.state.isControlCenterWarningVisible && (
                        <WarningAlert onCloseClick={() => this.setState({isControlCenterWarningVisible: false})}
                                      text='Too many sensors for selected control center are used'/>
                    )}

                    {!this.state.maxPlatesExceededAlertSeen && dhwCalculationOutput !== undefined && dhwCalculationOutput.maxPlatesExceeded.value && (
                        <WarningAlert onCloseClick={() => this.setState({ maxPlatesExceededAlertSeen: true })} text={dhwCalculationOutput.maxPlatesExceededWarningText.value} />
                    )}

                    <div className={`ball-loader ${this.state.isLoading ? 'visible' : ''}`}>
                        <div className="ball-loader-ball1"></div>
                        <div className="ball-loader-ball2"></div>
                        <div className="ball-loader-ball3"></div>
                    </div>

                    <div
                        className={[isTargetStepPricingOrSummary || isCustom ? 'column-12' : 'column-8', 'column-xs-12', 'configuration-editor-step-area-column'].join(' ')}
                        onScroll={this.onStepAreaColumnScroll} ref={r => this.stepAreaColumnScrollRef = r}>
                        <div className="card">
                            <div className="card-body configuration-editor-step-area" ref={this.stepAreaRef}>
                                <div className="configuration-editor-step-area-container">
                                    {this.renderAllSteps()}
                                </div>
                            </div>
                        </div>
                    </div>
                    {!isCustom && !isTargetStepPricingOrSummary && (
                        <div className="column-4 column-xs-12 configuration-editor-side-column">
                            <EditorDiagramCard diagram={store.getDiagram()}/>

                            {anyHeatingCalculationOutputs ? (
                                <div className="card configuration-editor-output-card">
                                    <div className="card-body">
                                        {heatingOutputVisible && this.renderHeatingOutputSection(1, heating1CalculationOutput, state => state.currentStepId === 'heating1')}
                                        {heatingOutputVisible && this.renderHeatingOutputSection(2, heating2CalculationOutput, state => state.currentStepId === 'heating2')}
                                        {heatingOutputVisible && this.renderHeatingOutputSection(3, heating3CalculationOutput, state => state.currentStepId === 'heating3')}
                                        {heatingTWSOutputVisible && this.renderHeatingTWSOutputSection(heatingTWSCalculationOutput, state => state.currentStepId === 'heatingTWS')}
                                        {dhwOutputVisible && this.renderDHWOutputSection(dhwCalculationOutput, state => state.currentStepId === 'dhw')}
                                    </div>
                                </div>
                            ) : ''}
                            {eesLevel ? (
                                <div className="card configuration-editor-output-card">
                                    <div className="card-body">
                                        {this.renderEESLevelSection(eesLevel)}
                                    </div>
                                </div>
                            ) : ''}
                        </div>
                    )}
                </div>
            )
        }

        renderStep(step, visible) {
            step = this.steps.find(x => x.slug === step.slug)
            let StepComponent = step.component

            return (
                <div key={step.slug} className={`configuration-editor-step-${step.slug.toLowerCase()}`}
                     style={{display: visible ? 'initial' : 'none'}}>
                    <StepComponent ref={step.ref}
                                   model={store.formModel}
                                   onFormModelChange={this.onFormModelChange}
                                   onModelResponseReceived={this.onAPIResponseReceived}
                    />
                </div>
            )
        }

        renderAllSteps() {
            const availableSteps = store.getAvailableSteps();
            const shouldStepBeVisible = (step, idx) => {
                return (
                    idx === this.state.currentStepIdx
                );
            }

            return availableSteps.map((step, idx) => this.renderStep(step, shouldStepBeVisible(step, idx)));
        }

        renderEESLevelSection(data) {
            return (
                <ExpandableContainer label={__('EES level')}>
                    <EESTable items={data.items}/>

                    <div className={`configuration-editor-ees-badge configuration-editor-ees-badge--${data.label}`}>
                        <div className="text">
                            {data.label}
                            <div>
                                {data.currentPoints} / {data.maximumPoints}
                            </div>
                        </div>
                    </div>
                </ExpandableContainer>
            )
        }

        renderSidebarOutputSection(label, heatExchangerName, capacity, rows, forceOpenFn, warnings) {
            if (rows === null || rows === undefined) {
                return (<React.Fragment></React.Fragment>)
            }

            return (
                <ExpandableContainer label={label} forceOpen={forceOpenFn(this.state)} warnings={warnings}>
                    <div className="responsive-table-container">
                        <table className="configuration-editor-output-table">
                            <tbody>
                            <React.Fragment>
                                {heatExchangerName && <tr className="transparent">
                                    <td>{__('Model')}</td>
                                    <td colSpan="3">{heatExchangerName}</td>
                                </tr>}
                                {capacity.value && <tr className="transparent">
                                    <td>{capacity.label}</td>
                                    <td colSpan="3">{FieldExtensions.getFieldValue(capacity)} {capacity.unit}</td>
                                </tr>}
                                <tr className="transparent">
                                    <td></td>
                                </tr>
                            </React.Fragment>
                            {React.Children.toArray(rows)}
                            </tbody>
                        </table>
                    </div>
                </ExpandableContainer>
            )
        }

        renderDHWOutputSection(output, forceOpenFn) {
            if (store.formModel.dhw) {
                const secondarySideFields = [
                    store.formModel.dhw.pump,
                    store.formModel.dhw.circulationPipeSize,
                    store.formModel.dhw.circulationValve,
                    store.formModel.dhw.coldWaterMeter
                ]

                let calculationRows = this.createCalculationOutputRows(output)
                let secondarySideRows = this.createSecondarySideOutputRows(secondarySideFields)
                let pseCircuitContentRows = this.createPrimarySideEquipmentCircuitOutputRows(store.formModel.primarySideEquipment.dhw)
                let rows = [calculationRows, secondarySideRows, pseCircuitContentRows]
                let warnings = output.heatExchangerOutput.map(x => x.primaryWarning);

                return this.renderSidebarOutputSection(`${store.formModel.dhw.name.value} ${__('output')}`, store.formModel.dhw.heatExchangerName.value, store.formModel.dhw.capacity, rows, forceOpenFn, warnings)
            }

            return <React.Fragment></React.Fragment>;
        }

        renderHeatingOutputSection(index, output, forceOpenFn) {
            let heatingModel = store.formModel['heating' + index]
            if (heatingModel) {
                const additionalFields = [
                    heatingModel.pump,
                    heatingModel.secondaryPipeSize,
                    heatingModel.valveSupply,
                    heatingModel.deaerator
                ]

                let calculationRows = this.createCalculationOutputRows(output)
                let secondarySideRows = this.createSecondarySideOutputRows(additionalFields)
                let pseCircuitContentRows = this.createPrimarySideEquipmentCircuitOutputRows(store.formModel.primarySideEquipment['heating' + index])
                let rows = [calculationRows, secondarySideRows, pseCircuitContentRows]
                let warnings = output.heatExchangerOutput.map(x => x.primaryWarning);

                return this.renderSidebarOutputSection(`${heatingModel.name.value} ${__('output')}`, heatingModel.heatExchangerName.value, heatingModel.capacity, rows, forceOpenFn, warnings)
            }

            return <React.Fragment></React.Fragment>;
        }

        renderHeatingTWSOutputSection(output, forceOpenFn) {
            let heatingModel = store.formModel.heatingTWS
            if (heatingModel) {
                let calculationRows = this.createCalculationOutputRows({heatExchangerOutput: output})
                let rows = [calculationRows]
                let warnings = output.map(x => x.primaryWarning);
                let heatExchangerName = (heatingModel.heatExchanger.items.find(x => x.value === heatingModel.heatExchanger.value) || {}).text;
                return this.renderSidebarOutputSection(`${heatingModel.name.value} ${__('output')}`, heatExchangerName, heatingModel.capacity, rows, forceOpenFn, warnings)
            }

            return <React.Fragment></React.Fragment>;
        }

        createCalculationOutputRows(output) {
            let result = []
            let length = output.heatExchangerOutput.length
            result.push(
                <tr key={-1} className="transparent">
                    <td></td>
                    <td className="configuration-editor-output-table-heading">{__('Primary')}</td>
                    <td className="configuration-editor-output-table-heading">{__('Secondary')}</td>
                    <td className="configuration-editor-output-table-heading">{__('Unit')}</td>
                </tr>
            )

            if (output.heatExchangerOutput) {
                for (let i = 0; i < length; i++) {
                    let e = output.heatExchangerOutput[i];
                    if (e.visible) {
                        result.push(
                            <tr className="calculation-output" style={length == i + 1 ? {borderBottom: 'none'} : {}}
                                key={i}>
                                <td>{e.name}</td>
                                <td>
                                    <div className="side">
                                        <div>{e.primary}</div>
                                        {e.primaryWarning && (
                                            <React.Fragment>
                                                <div className="input-warning-tooltip">
                                                    <Warning warning={e.primaryWarning}/>
                                                </div>
                                            </React.Fragment>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="side">
                                        <div>{e.secondary}</div>
                                        {e.secondaryWarning && (
                                            <React.Fragment>
                                                <div className="input-warning-tooltip">
                                                    <Warning warning={e.secondaryWarning}/>
                                                </div>
                                            </React.Fragment>
                                        )}
                                    </div>
                                </td>
                                <td>{e.unit}</td>
                            </tr>
                        )
                    }
                }
            }

            return <React.Fragment>
                <tr className="configuration-editor-output-table-section-heading">
                    <td colSpan="100%">{__('Heat exchanger')}</td>
                </tr>
                {result}</React.Fragment>
        }

        createSecondarySideOutputRows(fields) {
            let filtered = fields.filter(x => x.visible && x.value)
            let length = filtered.length
            let canShow = fields.some(x => x.value !== null)
            if (!canShow)
                return <React.Fragment></React.Fragment>

            return (
                <React.Fragment>
                    {length > 0 && <tr className="transparent">
                        <td></td>
                    </tr>}
                    <tr className="configuration-editor-output-table-section-heading">
                        <td colSpan="100%">{__('Secondary side')}</td>
                    </tr>
                    {filtered.map((field, i) =>
                        <tr className="secondary-side-output" key={i}
                            style={length == i + 1 ? {borderBottom: 'none'} : {}}>
                            <td>{field.label}</td>
                            <td colSpan="3">{FieldExtensions.getFieldValue(field)}</td>
                        </tr>)
                    }
                </React.Fragment>
            )
        }

        createPrimarySideEquipmentCircuitOutputRows(circuit) {
            let cvs = [circuit.cV1, circuit.cV2, circuit.cV3];
            let controlValvesPICV = cvs.map((c, i) => <td
                key={i}>{c && c.controlValvePICV && FieldExtensions.getFieldValue(c.controlValvePICV)}</td>)
            let controlValves = cvs.map((c, i) => <td
                key={i}>{c && c.controlValve && FieldExtensions.getFieldValue(c.controlValve)}</td>)
            let kvss = cvs.map((c, i) => <td key={i}>{c && c.kvs && FieldExtensions.getFieldValue(c.kvs)}</td>)
            let actuators = cvs.map((c, i) => <td
                key={i}>{c && c.actuatorType && FieldExtensions.getFieldValue(c.actuatorType)}</td>)

            let canShow = cvs.some(c => c && c.controlValve && c.controlValve.value)

            let canShowControlValvePICV = cvs.some(c => c && c.controlValvePICV && c.controlValvePICV.visible)
            let canShowControlValve = cvs.some(c => c && c.controlValve && c.controlValve.visible)
            let canShowKvs = cvs.some(c => c && c.kvs && c.kvs.visible)
            let canShowActuator = cvs.some(c => c && c.actuatorType && c.actuatorType.visible)

            const className = "control-valve-output"
            if (!canShow)
                return <React.Fragment></React.Fragment>
            return (
                <React.Fragment>
                    <tr className="transparent">
                        <td></td>
                    </tr>
                    <tr className="configuration-editor-output-table-section-heading">
                        <td colSpan="100%">{__('Control valve')}</td>
                    </tr>
                    <tr>
                        <td></td>
                        <td className="configuration-editor-output-table-heading">{circuit.cV1 && __('CV1')}</td>
                        <td className="configuration-editor-output-table-heading">{circuit.cV2 && __('CV2')}</td>
                        <td className="configuration-editor-output-table-heading">{circuit.cV3 && __('CV3')}</td>
                    </tr>
                    {canShowControlValve && <tr className={className}>
                        <td>{__('Control valve')}</td>
                        {controlValves}
                    </tr>}
                    {canShowControlValvePICV && <tr className={className}>
                        <td>{__('Control valve')}</td>
                        {controlValvesPICV}
                    </tr>}
                    {canShowKvs && <tr className={className}>
                        <td>{__('KVS')}</td>
                        {kvss}
                    </tr>}
                    {canShowActuator && <tr className={className}>
                        <td>{__('Actuator')}</td>
                        {actuators}
                    </tr>}
                </React.Fragment>
            )
        }

        isOnLastStep() {
            return this.state.currentStepIdx === store.getAvailableSteps().length - 1
        }

        isLoading() {
            return this.state.isLoading
        }

        isNewConfiguration() {
            return this.props.id.value == 0
        }
    }
)

export default ConfigurationEditor
